import { ContentPasteSearch, Quiz, Save, Settings } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Button, Grid, Stack } from "@mui/material";
import { makeStyles } from "@mui/styles";
import CustomStepper from "components/CustomizedMui/CustomStepper";
import GenericModal from "components/GenericModal";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import { assessmentSchema } from "data/assessmentForm";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import createAssessment from "layouts/pages/assessments/actions/createAssessment";
import uploadAssessmentExcelFile from "layouts/pages/assessments/actions/uploadAssessmentExcelFile";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { INTERVIEW_ENTITIES } from "utils/constants/entityOptions";
import BasicInfoStep from "./BasicInfoStep";
import QuestionsStep from "./QuestionsStep";
import SettingsStep from "./SettingsStep";

const useStyle = makeStyles({
  editor: {
    height: 150,
  },
  box: {
    overflow: "visible",
    height: 550,
  },
});

const { INTERVIEW_TYPES, STATES, INTERVIEW_STATUS } = INTERVIEW_ENTITIES;

const CommonIconProps = {
  fontSize: "large",
  color: "white",
};

const steps = [
  { id: 1, label: "Basic Info", icon: <ContentPasteSearch {...CommonIconProps} /> },
  {
    id: 2,
    label: "Questions",
    icon: <Quiz {...CommonIconProps} />,
  },
  {
    id: 3,
    label: "Settings",
    icon: <Settings {...CommonIconProps} />,
  },
];

const CreateAssessmentModal = ({ setOpen, open, selectedJob, applicants, onCreate }) => {
  const { company, currentLoggedUser } = useAppContextController();
  const resolver = useYupValidationResolver(assessmentSchema);
  const queryClient = useQueryClient();
  const classes = useStyle();
  const { enqueueSnackbar } = useSnackbar();
  const [currentStep, setCurrentStep] = useState(1);
  const [excelFiles, setExcelFiles] = useState(null);

  const defaultValues = {
    name: "",
    type: "",
    jobSlug: selectedJob?.jobSlug,
    description: selectedJob?.description || "",
    skills: [],
    questions: [],
    audience: [],
    notifyJobRecipients: "Yes",
    notifyCandidate: "No",
    showResultsToCandidate: "No",
    notificationRecipients: [],
  };

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    control,
    watch,
    getValues,
    trigger,
    setError,
    formState,
  } = useForm({ mode: "onBlur", resolver });
  const { errors, isSubmitting, dirtyFields, isDirty } = formState;
  const modalTitle = (
    <MDTypography
      variant="h5"
      color="dark"
      sx={{ mt: 2, mb: 1, ml: 2 }}
      // key={`note_title_${modalInfo.data.id}`}
    >
      Create Assessment for {selectedJob?.title}
    </MDTypography>
  );

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setCurrentStep(1);
      setExcelFiles(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob?._id, open]);

  const { mutateAsync: uploadSignatureImgMutation, isLoading: isLoadingUploadImg } = useMutation(
    async ({ assessmentId, templateFile, answerFile }) => {
      await uploadAssessmentExcelFile(assessmentId, templateFile, answerFile);
      // await updateProfile({ data: imgForm.data, userId: imgForm.data.userId });
    }
  );

  const createAssessmentMutation = useMutation(createAssessment, {
    onError: (err) => {
      enqueueSnackbar(`Something went wrong!  ${err?.response?.data?.message ?? err.toString()}`, {
        variant: "error",
      });
    },
    onSuccess: async (res, { data }) => {
      await queryClient.invalidateQueries(["jobAssessments"]);

      if (data.type === "Excel" && excelFiles.templateFile) {
        await uploadSignatureImgMutation({
          assessmentId: res?.data?._id,
          templateFile: excelFiles.templateFile,
        });
      }

      if (data.type === "Excel" && excelFiles.answerFile) {
        await uploadSignatureImgMutation({
          assessmentId: res?.data?._id,
          answerFile: excelFiles.answerFile,
        });
      }

      enqueueSnackbar("Assessment has been created!", { variant: "success" });
      if (onCreate) onCreate();
      setOpen(false);
    },
  });

  const createAssessmentHandler = async (values) => {
    const newAssessmentValues = { ...values };

    try {
      await createAssessmentMutation.mutateAsync({
        data: {
          ...newAssessmentValues,
          payRate: parseFloat(values.payRate),
          createAgent: currentLoggedUser._id,
        },
      });
    } catch (e) {
      //
    }
  };

  const handleSaveExcelFiles = ({ templateFile, answerFile }) => {
    setExcelFiles((current) => ({
      ...current,
      ...(templateFile !== undefined ? { templateFile } : {}),
      ...(answerFile !== undefined ? { answerFile } : {}),
    }));
  };

  const stepComponents = useMemo(
    () => [
      <BasicInfoStep
        clearErrors={clearErrors}
        setValue={setValue}
        control={control}
        getValues={getValues}
        errors={errors}
        trigger={trigger}
        watch={watch}
        setError={setError}
      />,
      <QuestionsStep
        clearErrors={clearErrors}
        setValue={setValue}
        control={control}
        getValues={getValues}
        errors={errors}
        trigger={trigger}
        watch={watch}
        jobSlug={selectedJob.jobSlug}
        excelFiles={excelFiles}
        onSubmitFiles={handleSaveExcelFiles}
      />,
      <SettingsStep
        clearErrors={clearErrors}
        setValue={setValue}
        control={control}
        getValues={getValues}
        errors={errors}
        trigger={trigger}
        watch={watch}
      />,
    ],
    [
      clearErrors,
      control,
      errors,
      excelFiles,
      getValues,
      selectedJob.jobSlug,
      setError,
      setValue,
      trigger,
      watch,
    ]
  );

  const modalBody = (
    <form onSubmit={handleSubmit(createAssessmentHandler)}>
      <Button
        sx={{
          position: "absolute",
          top: "0%",
          right: "0%",
          m: 1,
          p: 0,
        }}
        onClick={() => {
          reset(defaultValues);
          setOpen(false);
        }}
      >
        <CancelIcon color="secondary" />
      </Button>
      <CustomStepper activeStepId={currentStep} steps={steps} />
      <Grid container spacing={2}>
        {stepComponents[currentStep - 1] ?? stepComponents[0]}
        <Grid item xs={12} mt={5} textAlign="end">
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Box sx={{ display: "flex", gap: 4 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setOpen(false)}
                sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
              >
                Cancel
              </Button>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              {currentStep > 1 && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentStep((current) => current - 1);
                  }}
                  sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
                >
                  Back
                </Button>
              )}
              {currentStep < steps.length ? (
                <Button
                  type="button"
                  variant="contained"
                  style={{ color: "white" }}
                  sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (currentStep === 1) {
                      trigger().then((isValid) => {
                        if (isValid) {
                          setCurrentStep((current) => current + 1);
                        }
                      });
                    } else {
                      setCurrentStep((current) => current + 1);
                    }
                  }}
                >
                  Next
                </Button>
              ) : (
                <>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    style={{ color: "white" }}
                    sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
                    loading={createAssessmentMutation.isLoading}
                  >
                    Submit
                  </LoadingButton>
                </>
              )}
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </form>
  );

  return (
    <>
      <GenericModal
        open={open}
        setOpen={setOpen}
        body={modalBody}
        header={modalTitle}
        overflow="visible"
      />
    </>
  );
};

export default CreateAssessmentModal;
